import React, { useEffect, useState } from 'react';
import api, { formatApiError } from '../lib/api';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { MagnifyingGlass } from '@phosphor-icons/react';

const ACTION_COLORS = {
  login_success: 'text-emerald-700',
  login_failure: 'text-red-700',
  create_company: 'text-blue-700',
  import_csv: 'text-purple-700',
  pro_email_push: 'text-green-700',
  delete_company: 'text-red-700',
  update_settings: 'text-amber-700',
};

export default function AuditLogPage() {
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const params = { page, limit: 50 };
    if (search) params.search = search;
    api.get('/admin/audit-logs', { params }).then(r => {
      setLogs(r.data.items);
      setTotal(r.data.total);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, [page, search]);

  const totalPages = Math.ceil(total / 50);

  return (
    <div data-testid="audit-log-page">
      <div className="mb-6">
        <h1 className="font-heading text-3xl font-bold tracking-tighter text-gray-900">Audit Logs</h1>
        <p className="text-sm text-gray-500 mt-1">{total} log entries. Every important action is tracked: imports, approvals, exports, Pro-Email pushes, settings changes, and user management.</p>
      </div>

      <div className="mb-4">
        <div className="relative w-96">
          <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <Input placeholder="Search actions, details..." value={search} onChange={e => { setSearch(e.target.value); setPage(1); }} className="pl-9 bg-white border-gray-300" data-testid="audit-search" />
        </div>
      </div>

      <div className="border border-gray-200 rounded-md bg-white overflow-hidden">
        <table className="w-full">
          <thead>
            <tr className="bg-gray-50 border-b border-gray-200">
              <th className="text-xs font-medium text-gray-500 uppercase tracking-wider py-3 px-4 text-left">Time</th>
              <th className="text-xs font-medium text-gray-500 uppercase tracking-wider py-3 px-4 text-left">User</th>
              <th className="text-xs font-medium text-gray-500 uppercase tracking-wider py-3 px-4 text-left">Action</th>
              <th className="text-xs font-medium text-gray-500 uppercase tracking-wider py-3 px-4 text-left">Target</th>
              <th className="text-xs font-medium text-gray-500 uppercase tracking-wider py-3 px-4 text-left">Details</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan="5" className="py-12 text-center text-gray-400">Loading...</td></tr>
            ) : logs.length === 0 ? (
              <tr><td colSpan="5" className="py-12 text-center text-gray-400">No logs found</td></tr>
            ) : logs.map(log => (
              <tr key={log.id} className="border-b border-gray-100 hover:bg-gray-50">
                <td className="py-3 px-4 text-xs font-mono text-gray-500 whitespace-nowrap">{log.created_at ? new Date(log.created_at).toLocaleString() : '-'}</td>
                <td className="py-3 px-4 text-sm text-gray-700">{log.user_name || 'System'}</td>
                <td className="py-3 px-4">
                  <span className={`text-sm font-mono font-medium ${ACTION_COLORS[log.action] || 'text-gray-700'}`}>{log.action}</span>
                </td>
                <td className="py-3 px-4 text-xs font-mono text-gray-500">{log.target_type}{log.target_id ? `:${log.target_id.slice(0, 8)}` : ''}</td>
                <td className="py-3 px-4 text-sm text-gray-600 truncate max-w-xs">{log.details}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {totalPages > 1 && (
        <div className="flex items-center justify-between mt-4">
          <span className="text-sm text-gray-500">Page {page} of {totalPages}</span>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}>Previous</Button>
            <Button variant="outline" size="sm" onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}>Next</Button>
          </div>
        </div>
      )}
    </div>
  );
}
